import clsx from 'clsx';
import Flexsearch from 'flexsearch';
import { ReactNode, useCallback, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { CheckIcon, IconsFill } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';

import {
  Content,
  getContent,
  isContentId,
  useContent,
  useContentContext,
} from '../../../contexts/ContentContext';
import { PosDropdownItem } from '../PosDropdown';
import * as styles from './PosMultiSelect.css';
import { GroupedOptionsConfig } from './PosMultiSelect.types';

const EMPTY_CONTENT_DESIGNATOR = '____empty_content_____';
const USE_VIRTUOSO_MIN_OPTIONS_COUNT = 50;

export enum ToggleAllKeys {
  SelectAll = ContentId.SelectAll,
  ClearAll = ContentId.ClearAll,
}

export const isToggleAllValue = (value: string | ContentId | null): boolean => {
  return (
    !!value &&
    (value === ToggleAllKeys.SelectAll || value === ToggleAllKeys.ClearAll)
  );
};

// Special key prefix for group headers
const GROUP_HEADER_PREFIX = '__group_header__';

const isGroupHeaderKey = (key: string): boolean =>
  key.startsWith(GROUP_HEADER_PREFIX);

const getGroupLabelFromKey = (key: string): string =>
  key.replace(GROUP_HEADER_PREFIX, '');

export const PosMultiSelectOptions = ({
  contentHeight,
  selectedOptions,
  onChange,
  valueOptionsContent,
  valueOptionsIcon,
  valueOptionsPostfixIcon,
  emptySelectionText,
  searchText,
  sortMode = 'ascending',
  sortFn,
  useVirtualWindow: useVirtualWindowProp,
  expectedItemSizeForVirtualWindow = 40,
  flexSearchIndex,
  showSelectedOptionsFirst = true,
  showSelectAllOption,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  includeSelectedInSearch,
  valueOptionsDisplay,
  valueOptionsSort,
  groupedOptionsConfig,
}: {
  contentHeight: string;
  selectedOptions: string[];
  onChange: (o: string | null) => void;
  emptySelectionText?: string | ContentId;
  valueOptionsContent: Record<string, ContentId | string>;
  valueOptionsIcon?: Record<string, ReactNode>;
  valueOptionsPostfixIcon?: Record<string, ReactNode>;
  searchText?: string | null;
  sortMode?: 'ascending' | 'descending' | 'none';
  sortFn?: (a: string, b: string) => number;
  useVirtualWindow?: boolean;
  expectedItemSizeForVirtualWindow?: number;
  flexSearchIndex?: Flexsearch.Index | null;
  showSelectedOptionsFirst?: boolean;
  showSelectAllOption: boolean;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  includeSelectedInSearch?: boolean;
  valueOptionsDisplay?: Record<string, () => JSX.Element>;
  valueOptionsSort?: Record<string, string>;
  groupedOptionsConfig?: GroupedOptionsConfig;
}) => {
  const contentContext = useContentContext();
  const valueOptionsKeys = Object.keys(valueOptionsContent);
  const hasOptions = valueOptionsKeys.length > 0;

  const useVirtualWindow = useMemo<boolean>(() => {
    if (typeof useVirtualWindowProp === 'boolean') {
      return useVirtualWindowProp;
    }
    return (
      Object.keys(valueOptionsContent).length > USE_VIRTUOSO_MIN_OPTIONS_COUNT
    );
  }, [useVirtualWindowProp, valueOptionsContent]);

  const toggleKey = useMemo<ToggleAllKeys | undefined>(() => {
    if (showSelectAllOption) {
      const allOptionsSelected =
        valueOptionsKeys.length === selectedOptions.length;
      return allOptionsSelected
        ? ToggleAllKeys.ClearAll
        : ToggleAllKeys.SelectAll;
    }

    // Hide SelectAll option
    return selectedOptions.length > 0 ? ToggleAllKeys.ClearAll : undefined;
  }, [selectedOptions, valueOptionsKeys, showSelectAllOption]);

  const selectItems = useMemo(() => {
    const topOptions: string[] = [];
    if (emptySelectionText) {
      topOptions.push(EMPTY_CONTENT_DESIGNATOR);
    } else if (hasOptions && toggleKey) {
      topOptions.push(toggleKey);
    }

    let keysToRender = Object.keys(valueOptionsContent);
    const isSearching = searchText && searchText.trim().length > 0;

    if (isSearching) {
      keysToRender = flexSearchIndex?.search(searchText, {
        limit: keysToRender.length,
      }) as string[];
    }

    if (includeSelectedInSearch) {
      selectedOptions.forEach((item) => {
        if (!keysToRender.includes(item)) {
          keysToRender.push(item);
        }
      });
    }

    // Helper function to sort keys
    const sortKeys = (keys: string[]) => {
      return keys.toSorted((a: string, b: string) => {
        if (!sortMode || sortMode === 'none') {
          return 0;
        }

        const textA = valueOptionsSort
          ? valueOptionsSort[a]
          : getContent(valueOptionsContent[a], contentContext);
        const textB = valueOptionsSort
          ? valueOptionsSort[b]
          : getContent(valueOptionsContent[b], contentContext);

        let sortResult = textA
          .toLocaleUpperCase()
          .localeCompare(textB.toLocaleUpperCase());

        if (sortFn != null) {
          sortResult = sortFn(textA, textB);
        } else {
          const numberA = Number(textA);
          const numberB = Number(textB);
          if (!isNaN(numberA) && !isNaN(numberB)) {
            sortResult = numberA - numberB;
          }
        }

        return (sortMode === 'ascending' ? 1 : -1) * sortResult;
      });
    };

    let selectItems: string[];

    // Use grouped layout when config is provided and not searching
    if (groupedOptionsConfig && !isSearching) {
      selectItems = [];
      const keysSet = new Set(keysToRender);

      for (const group of groupedOptionsConfig.groups) {
        // Filter to only include keys that exist in keysToRender
        const groupKeys = group.optionKeys.filter((key) => keysSet.has(key));

        if (groupKeys.length > 0) {
          // Add group header
          selectItems.push(
            `${GROUP_HEADER_PREFIX}${group.labelContentId as string}`
          );
          // Add sorted group items
          selectItems.push(...sortKeys(groupKeys));
        }
      }
    } else {
      // Default flat list behavior
      selectItems = sortKeys(keysToRender ?? []);

      if (showSelectedOptionsFirst) {
        selectItems = [
          ...selectItems.filter((key) => selectedOptions.includes(key)),
          ...selectItems.filter((key) => !selectedOptions.includes(key)),
        ];
      }
    }

    return topOptions.concat(selectItems);
  }, [
    contentContext,
    emptySelectionText,
    flexSearchIndex,
    groupedOptionsConfig,
    hasOptions,
    includeSelectedInSearch,
    searchText,
    selectedOptions,
    showSelectedOptionsFirst,
    sortFn,
    sortMode,
    toggleKey,
    valueOptionsContent,
    valueOptionsSort,
  ]);

  const renderItem = useCallback(
    (key: string) => {
      if (key === EMPTY_CONTENT_DESIGNATOR) {
        return (
          <PosDropdownItem
            key={key}
            tabIndex={0}
            value={emptySelectionText}
            onClick={() => {
              onChange(null);
            }}
            keepOpenAfterSelection={true}
          >
            <Stack
              gap="m"
              justifyContent="spaceBetween"
              alignItems="center"
              width="full"
            >
              <div
                className={
                  selectedOptions.length === 0 ? styles.isSelected : undefined
                }
              >
                <Content id={emptySelectionText} />
              </div>

              {selectedOptions.length === 0 && (
                <CheckIcon
                  size={vars.iconSize.s}
                  fill={IconsFill.textSuccess}
                />
              )}
            </Stack>
          </PosDropdownItem>
        );
      }

      if (isToggleAllValue(key) && toggleKey) {
        return (
          <FilteredOptionItem
            key="toggle-all"
            optionKey={toggleKey}
            optionValue={toggleKey}
            onChange={() => onChange(toggleKey)}
            selectedOptions={selectedOptions}
            includeSelectedInSearch={includeSelectedInSearch}
          />
        );
      }

      // Render group header (non-selectable)
      if (isGroupHeaderKey(key)) {
        const labelContentId = getGroupLabelFromKey(key);
        return (
          <div key={key} className={styles.groupHeader}>
            <Content id={labelContentId} />
          </div>
        );
      }

      return (
        <FilteredOptionItem
          key={key}
          optionKey={key}
          optionValue={valueOptionsContent[key]}
          optionDisplay={valueOptionsDisplay?.[key]}
          prefixIcon={valueOptionsIcon?.[key]}
          postfixIcon={valueOptionsPostfixIcon?.[key]}
          onChange={(o) => onChange(o)}
          filterText={searchText}
          selectedOptions={selectedOptions}
          includeSelectedInSearch={includeSelectedInSearch}
        />
      );
    },
    [
      emptySelectionText,
      includeSelectedInSearch,
      onChange,
      searchText,
      selectedOptions,
      toggleKey,
      valueOptionsContent,
      valueOptionsIcon,
      valueOptionsPostfixIcon,
      valueOptionsDisplay,
    ]
  );

  return (
    <div className={styles.posMultiSelectOptionsContainer}>
      {useVirtualWindow ? (
        <Virtuoso
          overscan={10}
          style={{
            height: `clamp(100px, ${contentHeight}, ${
              selectItems.length * expectedItemSizeForVirtualWindow
            }px)`,
          }}
          defaultItemHeight={expectedItemSizeForVirtualWindow}
          data={selectItems}
          itemContent={(_, key) => renderItem(key)}
          endReached={() => {
            !isFetchingNextPage && hasNextPage && fetchNextPage?.();
          }}
        />
      ) : (
        <div
          className={styles.posMultiSelectOptionsContainerScroll}
          style={contentHeight ? { maxHeight: contentHeight } : {}}
        >
          {selectItems.map((key) => renderItem(key))}
        </div>
      )}
    </div>
  );
};

export const FilteredOptionItem = ({
  optionKey,
  optionValue,
  prefixIcon,
  postfixIcon,
  filterText,
  selectedOptions,
  onChange,
  includeSelectedInSearch,
  optionDisplay,
}: {
  optionKey: string;
  optionValue: string | ContentId;
  prefixIcon?: ReactNode;
  postfixIcon?: ReactNode;
  filterText?: string | null;
  selectedOptions: string[];
  onChange: (o: string | null) => void;
  includeSelectedInSearch?: boolean;
  optionDisplay?: () => JSX.Element;
}) => {
  const isSelected = selectedOptions.includes(optionKey);

  const contentWrapper = useCallback(
    (
      key: string | ContentId,
      content: string | JSX.Element,
      isSelected: boolean,
      display?: () => JSX.Element
    ) => {
      return (
        <PosDropdownItem
          tabIndex={0}
          key={key}
          onClick={() => {
            onChange(key);
          }}
          onKeyUp={(k) => {
            if (k.key === 'Enter') {
              onChange(key);
            }
          }}
          keepOpenAfterSelection={true}
          data-testid={`filter-option-${key}`}
          data-isselected={isSelected}
        >
          <Stack
            gap="m"
            justifyContent="spaceBetween"
            alignItems="center"
            width="full"
          >
            <Stack
              gap="m"
              alignItems="center"
              className={styles.optionContentContainer}
            >
              <div>{prefixIcon}</div>
              <div
                className={clsx(
                  styles.optionContent,
                  isSelected && styles.isSelected
                )}
              >
                {display ? display() : content}
              </div>
            </Stack>
            <Stack gap="m" alignItems="center">
              {postfixIcon && <div>{postfixIcon}</div>}
              {isSelected && (
                <CheckIcon
                  size={vars.iconSize.s}
                  minSize={vars.iconSize.s}
                  fill={IconsFill.textSuccess}
                />
              )}
            </Stack>
          </Stack>
        </PosDropdownItem>
      );
    },
    [onChange, postfixIcon, prefixIcon]
  );

  if (filterText) {
    if (isContentId(optionValue)) {
      return (
        <FilteredContent
          key={optionKey}
          optionKey={optionKey}
          id={optionValue}
          contentWrapper={contentWrapper}
          filterText={filterText}
          isSelected={isSelected}
        />
      );
    } else {
      // Split filterText into individual words, removing extra whitespace.
      const filterWords = filterText.trim().split(/\s+/);

      // Check if every word in filterText is present in optionValue (case-insensitive).
      const matchesAllWords = filterWords.every((word) =>
        optionValue?.toUpperCase().includes(word.toUpperCase())
      );

      if (matchesAllWords || includeSelectedInSearch) {
        return contentWrapper(
          optionKey,
          optionValue,
          isSelected,
          optionDisplay
        );
      }
      return null;
    }
  }

  return contentWrapper(
    optionKey,
    <Content id={optionValue} />,
    isSelected,
    optionDisplay
  );
};

export const FilteredContent = ({
  optionKey,
  id,
  filterText,
  contentWrapper,
  isSelected,
}: {
  optionKey: string | ContentId;
  id: ContentId;
  filterText: string;
  isSelected: boolean;
  contentWrapper: (
    key: string | ContentId,
    content: string | JSX.Element,
    isSelected: boolean
  ) => JSX.Element;
}) => {
  const text = useContent(id);
  if (!text.toUpperCase().includes(filterText.toUpperCase())) {
    return null;
  }

  return contentWrapper(optionKey, <Content id={id} />, isSelected);
};
